import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
    Canvas,
    FabricImage,
    FabricObject,
    Line,
    Path,
    PencilBrush,
    Point,
    StaticCanvas,
} from "fabric";

/**
 * Nav2-oriented keepout mask editing (raster BMP aligned with the static map).
 * Pen colors map to occupied / cost / free per your Nav2 filter pipeline.
 */
import MapView from "./MapView";
import { useMap } from "providers/navigation/MapProvider";
import { useToast } from "providers/ToastProvider";
import {
    getStorageStcmEditFromLocation,
    getStcmPreviewBmpUrl,
    getStorageMapPreviewBmpUrl,
    mapFilterGeometryFromMetadata,
    postSaveVariantImage,
    storageMapRowLabel,
} from "lib/consts/stcmMapList";
import { isFilterMaskDebugEnabled, logMapEditorSaveDebug } from "lib/debug/filterMaskDebug";
import {
    applyFabricImageFillGrid,
    buildMaskExportImages,
    exportNav2CellAlignedMaskFromEditor,
    fetchPreviewImageBlob,
    loadBlobImageDimensions,
    mergeMapAndAnnotationsToBlob,
    sketchBoundsFromCanvas,
} from "lib/navigation/mapEditorExport";
import {
    computeCanvasContainToFillGridTransform,
    editorUsesGridFillLayout,
    isAurora2dExportGridSource,
    isCompleteMapEditorGridMetadata,
    isMapEditorGridCellAligned,
    liveOccupancyGridDims,
    loadImageNaturalDimensions,
    pickMapEditorGeometrySource,
    pickMapEditorGridMetadata,
    previewMatchesGridPixelSize,
    previewNeedsNav2ResampleExport,
    rasterSourceDimsAlignedToLayout,
    resolveMapEditorGridDims,
    variantAspectDiffersFromLayout,
    type MapEditorGridDims,
    type MapMetadataLike,
    type NaturalImageDims,
} from "lib/navigation/mapEditorGrid";

/** Pen colors for keep-out drawing (pencil mode). */
export type MapEditorPenColor = "black" | "grey" | "white";

export const MAP_EDITOR_PEN_HEX: Record<MapEditorPenColor, string> = {
    black: "#000000",
    grey: "#7f7f7f",
    white: "#ffffff",
};

/** Opaque stroke for `destination-out` — removes pixels from the annotation layer only (map is separate). */
const PIXEL_ERASE_BRUSH_STROKE = "#000000";

/**
 * Normal pencil: Fabric's incremental free-draw path does not call `_setBrushStyles` on every
 * segment, so `globalCompositeOperation` can stay `destination-out` after the pixel eraser.
 */
class SourceOverPencilBrush extends PencilBrush {
    _setBrushStyles(ctx: CanvasRenderingContext2D) {
        super._setBrushStyles(ctx);
        ctx.globalCompositeOperation = "source-over";
    }
}

/**
 * Pixel eraser: `destination-out` on Fabric's upper `contextTop` only composites with that layer;
 * committed strokes live on the lower canvas, so live erase must update a temp Path on the main
 * canvas. On mouse up the real path is finalized like a normal pencil stroke.
 */
class PixelEraseLiveBrush extends PencilBrush {
    private _previewPath: Path | null = null;

    private _removePreviewPath() {
        if (this._previewPath) {
            this.canvas.remove(this._previewPath);
            this._previewPath = null;
        }
    }

    private _syncPreviewPath() {
        this._removePreviewPath();
        const pathData = this.convertPointsToSVGPath(this._points);
        const path = this.createPath(pathData);
        path.set({
            globalCompositeOperation: "destination-out",
            stroke: PIXEL_ERASE_BRUSH_STROKE,
        });
        this.canvas.add(path);
        this._previewPath = path;
        this.canvas.requestRenderAll();
    }

    onMouseDown(pointer: Point, opt: Parameters<PencilBrush["onMouseDown"]>[1]) {
        const { e } = opt;
        if (!this.canvas._isMainEvent(e)) {
            return;
        }
        this.drawStraightLine =
            !!this.straightLineKey &&
            Boolean((e as unknown as Record<string, boolean>)[String(this.straightLineKey)]);
        this._prepareForDrawing(pointer);
        this._addPoint(pointer);
        // Intentionally no _render() — do not draw erase preview on contextTop (wrong composite target).
    }

    onMouseMove(pointer: Point, opt: Parameters<PencilBrush["onMouseMove"]>[1]) {
        const { e } = opt;
        if (!this.canvas._isMainEvent(e)) {
            return;
        }
        this.drawStraightLine =
            !!this.straightLineKey &&
            Boolean((e as unknown as Record<string, boolean>)[String(this.straightLineKey)]);
        if (this.limitedToCanvasSize === true && this._isOutSideCanvas(pointer)) {
            return;
        }
        if (this._addPoint(pointer) && this._points.length > 1) {
            this._syncPreviewPath();
        }
    }

    onMouseUp(opt: Parameters<PencilBrush["onMouseUp"]>[0]) {
        this._removePreviewPath();
        this.canvas.clearContext(this.canvas.contextTop);
        return super.onMouseUp(opt);
    }
}

function createFreeDrawingBrush(
    canvas: Canvas,
    pixelErase: boolean,
    penColor: MapEditorPenColor,
    widthPx: number,
): PencilBrush {
    const brush = pixelErase ? new PixelEraseLiveBrush(canvas) : new SourceOverPencilBrush(canvas);
    brush.color = pixelErase
        ? PIXEL_ERASE_BRUSH_STROKE
        : MAP_EDITOR_PEN_HEX[penColor] ?? MAP_EDITOR_PEN_HEX.black;
    brush.width = widthPx;
    return brush;
}

const MIN_VIEW_ZOOM = 0.2;
const MAX_VIEW_ZOOM = 8;
const VIEW_ZOOM_STEP = 1.2;
const WHEEL_ZOOM_FACTOR = 1.06;

function touchPairDistance(touches: TouchList): number {
    if (touches.length < 2) return 0;
    const a = touches[0];
    const b = touches[1];
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

export type MapEditorHandle = {
    saveBmp: () => Promise<void>;
    undoSketch: () => void;
    clearAllSketch: () => void;
    zoomViewIn: () => void;
    zoomViewOut: () => void;
    resetViewZoom: () => void;
};

export type MapEditorBrushPreset = "s" | "m" | "l";

/** Brush width in pixels for the given preset and map image size (for UI hints). */
export function brushWidthFromPreset(
    preset: MapEditorBrushPreset,
    mapW: number,
    mapH: number,
): number {
    const base = Math.max(2, Math.round(Math.min(mapW, mapH) / 400));
    const mult = preset === "s" ? 0.65 : preset === "l" ? 1.85 : 1;
    return Math.max(1, Math.round(base * mult));
}

/** Slider 0–100 → brush width in pixels (wider min/max than legacy thin↔thick). */
export function brushWidthFromBrushSlider(size: number, mapW: number, mapH: number): number {
    const base = Math.max(2, Math.round(Math.min(mapW, mapH) / 400));
    const minW = Math.max(1, Math.round(base * 0.32));
    const maxW = Math.max(minW + 1, Math.round(base * 3.1));
    const t = Math.min(100, Math.max(0, size)) / 100;
    return Math.round(minW + (maxW - minW) * t);
}

/** Clear imperative DOM under the editor scrollport (Fabric dispose() may already remove nodes). */
function safeClearElement(el: HTMLElement): void {
    try {
        while (el.firstChild) {
            el.removeChild(el.firstChild);
        }
    } catch {
        try {
            el.textContent = "";
        } catch {
            /* ignore */
        }
    }
}

/** Nav2 filter “free” — letterbox areas use this instead of black (occupied). */
const FILTER_MASK_FREE_HEX = MAP_EDITOR_PEN_HEX.white;

export type MapEditorProps = {
    drawMode?: boolean;
    eraseMode?: boolean;
    /** When true, drag pans the view; drawing and stroke erase are disabled. */
    panMode?: boolean;
    /** When true, brush removes annotation paint only (does not paint over the map image). */
    pixelEraseMode?: boolean;
    /** When true, click-drag draws a straight line segment. */
    lineMode?: boolean;
    saveBmpRelPath?: string;
    onSaveBusyChange?: (busy: boolean) => void;
    onSketchToolbarState?: (s: { canUndo: boolean }) => void;
    /** 0–100; drives Fabric free-drawing brush width. */
    brushSize?: number;
    /** Stroke color when painting with the pen (draw mode). */
    penColor?: MapEditorPenColor;
    /** Fired when storage map canvas pixel size is known (for brush width ↔ meters hints). */
    onStorageCanvasDimensions?: (
        dims: { w: number; h: number; resolution?: number; cellAligned?: boolean } | null,
    ) => void;
};

/**
 * MapEditor: live map from MapProvider, or a storage map BMP when opened via
 * navigate(..., { state: { storageStcmEdit: { baseUrl, relPath } } }).
 * Storage annotations use Fabric.js (free drawing + tap-to-delete strokes).
 */
const MapEditor = forwardRef<MapEditorHandle, MapEditorProps>(function MapEditor(
    {
        drawMode = false,
        eraseMode = false,
        panMode = false,
        pixelEraseMode = false,
        lineMode = false,
        saveBmpRelPath = "",
        onSaveBusyChange,
        onSketchToolbarState,
        brushSize = 50,
        penColor = "black",
        onStorageCanvasDimensions,
    },
    ref,
) {
    const location = useLocation();
    const storageEdit = useMemo(
        () => getStorageStcmEditFromLocation(location.state),
        [location.state],
    );
    const { addToast } = useToast();

    const { mapImageUrl, mapMetadata } = useMap();
    /** Storage edit uses preview BMPs only — avoid depending on live mapImageUrl identity. */
    const storageEditActive = Boolean(storageEdit?.baseUrl && storageEdit?.relPath);

    /** Grid transform frozen for this editor session (ignores later /map_metadata ticks). */
    const [frozenGridMetadata, setFrozenGridMetadata] = useState<MapMetadataLike>(null);
    const liveMetaCapturedRef = useRef(false);
    /** Natural size of the parent STCM companion BMP (geometry reference). */
    const companionNaturalRef = useRef<NaturalImageDims>(null);
    /** Natural size of the variant raster under edit (.png), if any. */
    const variantNaturalRef = useRef<NaturalImageDims>(null);
    /** Natural size used for letterbox layout and Nav2 export (companion STCM BMP when available). */
    const editorLayoutNaturalRef = useRef<NaturalImageDims>(null);
    const usesCompanionGeometryRef = useRef(false);
    const geometryPreviewUrlRef = useRef<string | null>(null);

    const editorGridMetadata = frozenGridMetadata;

    const [storageGrid, setStorageGrid] = useState<MapEditorGridDims | null>(null);
    const [storagePreviewError, setStoragePreviewError] = useState(false);

    const [saveBusy, setSaveBusy] = useState(false);
    const saveInFlightRef = useRef(false);
    const [fabricReady, setFabricReady] = useState(false);

    const wrapRef = useRef<HTMLDivElement | null>(null);
    /** Upper canvas: mask strokes only (transparent under). */
    const fabricCanvasRef = useRef<Canvas | null>(null);
    /** Lower canvas: static map image only. */
    const mapStaticCanvasRef = useRef<StaticCanvas | null>(null);
    const drawModeRef = useRef(drawMode);
    const eraseModeRef = useRef(eraseMode);
    const panModeRef = useRef(panMode);
    const pixelEraseModeRef = useRef(pixelEraseMode);
    const lineModeRef = useRef(lineMode);
    const penColorRef = useRef(penColor);
    const brushSizeRef = useRef(brushSize);
    penColorRef.current = penColor;
    brushSizeRef.current = brushSize;
    const [panPointerDown, setPanPointerDown] = useState(false);
    /** Multiplier on “fit to panel” CSS size (1 = fit). */
    const viewZoomRef = useRef(1);
    /** Last CSS size applied — skip Fabric setDimensions when unchanged. */
    const lastDisplayCssRef = useRef<{ w: number; h: number } | null>(null);
    const displayScaleRafRef = useRef<number | null>(null);

    /** Center scroll on the map (scrollport must not use flex centering — that breaks scroll ranges). */
    const centerMapScroll = useCallback(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const maxL = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
                const maxT = Math.max(0, wrap.scrollHeight - wrap.clientHeight);
                wrap.scrollLeft = maxL / 2;
                wrap.scrollTop = maxT / 2;
            });
        });
    }, []);

    useEffect(() => {
        drawModeRef.current = drawMode;
        eraseModeRef.current = eraseMode;
        panModeRef.current = panMode;
        pixelEraseModeRef.current = pixelEraseMode;
        lineModeRef.current = lineMode;
    }, [drawMode, eraseMode, panMode, pixelEraseMode, lineMode]);

    /** CSS scale only — backing store stays at native BMP size so saves stay pixel-accurate. */
    const applyDisplayScale = useCallback(() => {
        const wrap = wrapRef.current;
        const drawCanvas = fabricCanvasRef.current;
        const mapCanvas = mapStaticCanvasRef.current;
        if (!wrap || !drawCanvas || !mapCanvas) return;
        const iw = drawCanvas.getWidth();
        const ih = drawCanvas.getHeight();
        const { clientWidth, clientHeight } = wrap;
        if (clientWidth < 8 || clientHeight < 8) return;
        const inset = 16;
        const availW = Math.max(8, clientWidth - inset * 2);
        const availH = Math.max(8, clientHeight - inset * 2);
        const fitScale = Math.min(availW / iw, availH / ih);
        const scale = fitScale * viewZoomRef.current;
        const cssW = Math.max(1, Math.floor(iw * scale));
        const cssH = Math.max(1, Math.floor(ih * scale));
        const prev = lastDisplayCssRef.current;
        if (prev && prev.w === cssW && prev.h === cssH) return;
        lastDisplayCssRef.current = { w: cssW, h: cssH };
        const dim = { width: `${cssW}px`, height: `${cssH}px` };
        drawCanvas.setDimensions(dim, { cssOnly: true });
        mapCanvas.setDimensions(dim, { cssOnly: true });
        // Match scroll box to displayed size — native iw×ih would inflate overflow and break centering.
        const inner = wrap.querySelector("#map-editor-map-stack") as HTMLElement | null;
        if (inner) {
            inner.style.width = `${cssW}px`;
            inner.style.height = `${cssH}px`;
        }
    }, []);

    /** Coalesce ResizeObserver / pinch bursts onto one rAF; Fabric resizing is expensive. */
    const scheduleDisplayScale = useCallback(() => {
        if (displayScaleRafRef.current != null) return;
        displayScaleRafRef.current = requestAnimationFrame(() => {
            displayScaleRafRef.current = null;
            applyDisplayScale();
        });
    }, [applyDisplayScale]);

    const zoomViewIn = useCallback(() => {
        viewZoomRef.current = Math.min(
            MAX_VIEW_ZOOM,
            Number((viewZoomRef.current * VIEW_ZOOM_STEP).toFixed(3)),
        );
        lastDisplayCssRef.current = null;
        applyDisplayScale();
    }, [applyDisplayScale]);

    const zoomViewOut = useCallback(() => {
        viewZoomRef.current = Math.max(
            MIN_VIEW_ZOOM,
            Number((viewZoomRef.current / VIEW_ZOOM_STEP).toFixed(3)),
        );
        lastDisplayCssRef.current = null;
        applyDisplayScale();
    }, [applyDisplayScale]);

    const resetViewZoom = useCallback(() => {
        viewZoomRef.current = 1;
        lastDisplayCssRef.current = null;
        applyDisplayScale();
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                centerMapScroll();
            });
        });
    }, [applyDisplayScale, centerMapScroll]);

    const sketchObjectCount = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return 0;
        return canvas.getObjects().length;
    }, []);

    const emitToolbarState = useCallback(() => {
        onSketchToolbarState?.({ canUndo: sketchObjectCount() > 0 });
    }, [onSketchToolbarState, sketchObjectCount]);

    /**
     * Same URL as Map Manager preview (`getStorageMapPreviewBmpUrl` for the edited row).
     * Do not use live `/map/compressed` — Control shows that with Leaflet X-mirror.
     */
    const storagePreviewApiUrl = useMemo(() => {
        if (!storageEdit?.baseUrl || !storageEdit?.relPath) return null;
        return getStorageMapPreviewBmpUrl(storageEdit.baseUrl, storageEdit.relPath);
    }, [storageEdit?.baseUrl, storageEdit?.relPath]);

    const parentStcmRelPath = useMemo(() => {
        const parent = storageEdit?.parentStcmRelPath?.replace(/\\/g, "/").trim();
        if (parent) return parent;
        const rel = storageEdit?.relPath?.replace(/\\/g, "/").trim() ?? "";
        return rel.toLowerCase().endsWith(".stcm") ? rel : null;
    }, [storageEdit?.parentStcmRelPath, storageEdit?.relPath]);

    const companionPreviewApiUrl = useMemo(() => {
        if (!storageEdit?.baseUrl || !parentStcmRelPath) return null;
        if (parentStcmRelPath === storageEdit?.relPath?.replace(/\\/g, "/").trim()) {
            return null;
        }
        return getStcmPreviewBmpUrl(storageEdit.baseUrl, parentStcmRelPath);
    }, [storageEdit?.baseUrl, storageEdit?.relPath, parentStcmRelPath]);

    /** New map in editor: reset frozen metadata and grid (not on every /map_metadata message). */
    useEffect(() => {
        if (!storagePreviewApiUrl) {
            liveMetaCapturedRef.current = false;
            setFrozenGridMetadata(null);
            setStorageGrid(null);
            setStoragePreviewError(false);
            variantNaturalRef.current = null;
            companionNaturalRef.current = null;
            editorLayoutNaturalRef.current = null;
            usesCompanionGeometryRef.current = false;
            geometryPreviewUrlRef.current = null;
            return;
        }
        const initial = pickMapEditorGridMetadata(mapMetadata, storageEdit?.liveMapGrid);
        liveMetaCapturedRef.current = isCompleteMapEditorGridMetadata(initial);
        setFrozenGridMetadata(initial);
        setStorageGrid(null);
        setStoragePreviewError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only when opening a different storage map
    }, [storagePreviewApiUrl, storageEdit?.relPath]);

    /** Capture live /map_metadata once when the editor opens (size does not change while editing). */
    useEffect(() => {
        if (liveMetaCapturedRef.current) return;
        const merged = pickMapEditorGridMetadata(mapMetadata, storageEdit?.liveMapGrid);
        if (!isCompleteMapEditorGridMetadata(merged)) return;
        liveMetaCapturedRef.current = true;
        setFrozenGridMetadata(merged);
    }, [mapMetadata, storageEdit?.liveMapGrid]);

    useEffect(() => {
        if (!storagePreviewApiUrl) {
            return;
        }

        let cancelled = false;

        void (async () => {
            const [previewNatural, companionNatural] = await Promise.all([
                loadImageNaturalDimensions(storagePreviewApiUrl),
                companionPreviewApiUrl &&
                    companionPreviewApiUrl !== storagePreviewApiUrl
                    ? loadImageNaturalDimensions(companionPreviewApiUrl)
                    : Promise.resolve(null),
            ]);
            if (cancelled) return;
            variantNaturalRef.current = previewNatural;
            companionNaturalRef.current = companionNatural;
            const geometry = pickMapEditorGeometrySource(
                storagePreviewApiUrl,
                previewNatural,
                companionPreviewApiUrl,
                companionNatural,
            );
            if (geometry) {
                editorLayoutNaturalRef.current = geometry.layoutNatural;
                usesCompanionGeometryRef.current = geometry.usesCompanionGeometry;
                geometryPreviewUrlRef.current = geometry.geometryPreviewUrl;
            } else {
                editorLayoutNaturalRef.current = previewNatural;
                usesCompanionGeometryRef.current = false;
                geometryPreviewUrlRef.current = storagePreviewApiUrl;
            }
            const grid = resolveMapEditorGridDims(
                frozenGridMetadata,
                previewNatural,
                companionNatural,
                storageEdit?.liveMapGrid,
            );
            if (grid) {
                setStorageGrid((prev) => {
                    if (
                        prev &&
                        prev.width === grid.width &&
                        prev.height === grid.height
                    ) {
                        return prev;
                    }
                    return grid;
                });
            }
            if (!previewNatural) {
                if (!grid) {
                    setStoragePreviewError(true);
                }
                return;
            }
            if (!grid) {
                setStoragePreviewError(true);
                return;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        storagePreviewApiUrl,
        companionPreviewApiUrl,
        storageEdit?.liveMapGrid,
        frozenGridMetadata,
    ]);

    const storageDims = useMemo(
        () =>
            storageGrid
                ? { w: storageGrid.width, h: storageGrid.height }
                : null,
        [storageGrid],
    );

    useEffect(() => {
        if (!storageDims || !storageGrid) {
            onStorageCanvasDimensions?.(null);
            return;
        }
        const res = editorGridMetadata?.resolution;
        const cellAligned = isMapEditorGridCellAligned(storageGrid.source);
        onStorageCanvasDimensions?.({
            w: storageDims.w,
            h: storageDims.h,
            ...(typeof res === "number" && Number.isFinite(res) && res > 0
                ? { resolution: res }
                : {}),
            cellAligned,
        });
    }, [storageDims, storageGrid, editorGridMetadata?.resolution, onStorageCanvasDimensions]);

    const storageDerived = useMemo(() => {
        if (!storageEdit?.baseUrl || !storageEdit?.relPath || !storageDims) return null;
        const relPath = storageEdit.relPath;
        const res =
            typeof editorGridMetadata?.resolution === "number" &&
                Number.isFinite(editorGridMetadata.resolution) &&
                editorGridMetadata.resolution > 0
                ? editorGridMetadata.resolution
                : 0.05;
        const origin = editorGridMetadata?.origin ?? { x: 0, y: 0 };
        return {
            mapMetadata: {
                id: `stcm-storage:${relPath}`,
                name: storageMapRowLabel(relPath),
                created_time: 0,
                desc: "",
                width: storageDims.w,
                height: storageDims.h,
                resolution: res,
                origin: { x: origin.x ?? 0, y: origin.y ?? 0 },
                ...(typeof editorGridMetadata?.originYaw === "number"
                    ? { originYaw: editorGridMetadata.originYaw }
                    : {}),
            },
        };
    }, [storageEdit, storageDims, editorGridMetadata]);

    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap || !storagePreviewApiUrl || !storageDims || !storageGrid) return;
        if (!isMapEditorGridCellAligned(storageGrid.source)) {
            setFabricReady(false);
            return;
        }

        let disposed = false;
        let drawCanvas: Canvas | null = null;
        let mapCanvas: StaticCanvas | null = null;
        let ro: ResizeObserver | null = null;

        const releaseMountCanvases = () => {
            if (fabricCanvasRef.current === drawCanvas) {
                fabricCanvasRef.current = null;
            }
            if (mapStaticCanvasRef.current === mapCanvas) {
                mapStaticCanvasRef.current = null;
            }
            try {
                drawCanvas?.dispose();
            } catch {
                /* Fabric may already have removed nodes */
            }
            try {
                mapCanvas?.dispose();
            } catch {
                /* ignore */
            }
            drawCanvas = null;
            mapCanvas = null;
        };

        setFabricReady(false);

        const mount = async () => {
            safeClearElement(wrap);
            // Flex centering lives inside the scrollport — not on the scroll element (avoids broken pan/scroll).
            const centerRail = document.createElement("div");
            centerRail.id = "map-editor-center-rail";
            centerRail.className =
                "box-border flex min-h-full min-w-full w-max max-w-none shrink-0 items-center justify-center p-4";
            const inner = document.createElement("div");
            inner.id = "map-editor-map-stack";
            inner.className = "relative inline-block max-w-none shrink-0";
            centerRail.appendChild(inner);
            wrap.appendChild(centerRail);

            const { w: iw, h: ih } = storageDims;
            // Size is set in applyDisplayScale to scaled CSS px (matches canvas display size).

            const lowerWrap = document.createElement("div");
            lowerWrap.className = "relative z-0";
            const upperWrap = document.createElement("div");
            upperWrap.className = "absolute left-0 top-0 z-10";
            upperWrap.style.pointerEvents = "auto";

            const lowerEl = document.createElement("canvas");
            const upperEl = document.createElement("canvas");
            lowerWrap.appendChild(lowerEl);
            upperWrap.appendChild(upperEl);
            inner.appendChild(lowerWrap);
            inner.appendChild(upperWrap);

            mapCanvas = new StaticCanvas(lowerEl, {
                width: iw,
                height: ih,
                backgroundColor: FILTER_MASK_FREE_HEX,
                enableRetinaScaling: false,
                renderOnAddRemove: true,
            });
            drawCanvas = new Canvas(upperEl, {
                width: iw,
                height: ih,
                selection: false,
                preserveObjectStacking: true,
                enableRetinaScaling: false,
                renderOnAddRemove: false,
            });
            if (disposed) {
                releaseMountCanvases();
                return;
            }

            mapStaticCanvasRef.current = mapCanvas;
            fabricCanvasRef.current = drawCanvas;

            try {
                const geometry = pickMapEditorGeometrySource(
                    storagePreviewApiUrl,
                    variantNaturalRef.current,
                    companionPreviewApiUrl,
                    companionNaturalRef.current,
                );
                if (geometry) {
                    editorLayoutNaturalRef.current = geometry.layoutNatural;
                    usesCompanionGeometryRef.current = geometry.usesCompanionGeometry;
                    geometryPreviewUrlRef.current = geometry.geometryPreviewUrl;
                }

                const layoutNat = editorLayoutNaturalRef.current;
                if (!layoutNat) {
                    throw new Error("Map layout dimensions unknown");
                }
                const layoutW = layoutNat.w;
                const layoutH = layoutNat.h;

                const geometryUrl =
                    geometryPreviewUrlRef.current ?? storagePreviewApiUrl;
                const variantOverlayUrl =
                    geometry?.variantPreviewUrl ??
                    (companionPreviewApiUrl &&
                        companionPreviewApiUrl !== storagePreviewApiUrl
                        ? storagePreviewApiUrl
                        : null);

                // No crossOrigin: same as Map Manager <img> — Fabric would fail to decode if the
                // server omits Access-Control-Allow-Origin on image requests.
                const bgImg = await FabricImage.fromURL(geometryUrl);
                if (disposed || !drawCanvas || !mapCanvas) {
                    releaseMountCanvases();
                    return;
                }

                const bgOriginal = bgImg.getOriginalSize();
                const bgW = bgOriginal.width || bgImg.width || layoutW;
                const bgH = bgOriginal.height || bgImg.height || layoutH;
                const bgSrc = rasterSourceDimsAlignedToLayout(
                    { w: bgW, h: bgH },
                    layoutNat,
                );
                applyFabricImageFillGrid(bgImg, bgSrc.w, bgSrc.h, iw, ih);
                mapCanvas.add(bgImg);

                if (variantOverlayUrl) {
                    const maskImg = await FabricImage.fromURL(variantOverlayUrl);
                    if (disposed || !drawCanvas || !mapCanvas) {
                        releaseMountCanvases();
                        return;
                    }
                    const maskOriginal = maskImg.getOriginalSize();
                    const maskW = maskOriginal.width || maskImg.width || 1;
                    const maskH = maskOriginal.height || maskImg.height || 1;
                    const maskVariantNatural = { w: maskW, h: maskH };
                    const maskSrc = rasterSourceDimsAlignedToLayout(
                        maskVariantNatural,
                        layoutNat,
                    );
                    applyFabricImageFillGrid(
                        maskImg,
                        maskSrc.w,
                        maskSrc.h,
                        iw,
                        ih,
                    );
                    maskImg.set({ opacity: 0.92 });
                    mapCanvas.add(maskImg);
                }

                mapCanvas.requestRenderAll();

                const px =
                    pixelEraseModeRef.current && !drawModeRef.current;
                drawCanvas.freeDrawingBrush = createFreeDrawingBrush(
                    drawCanvas,
                    px,
                    penColorRef.current,
                    brushWidthFromBrushSlider(brushSize, iw, ih),
                );

                drawCanvas.on("object:added", () => emitToolbarState());
                drawCanvas.on("object:removed", () => emitToolbarState());
                drawCanvas.on("path:created", (opt) => {
                    const path = (
                        opt as {
                            path?: FabricObject & { set?: (v: object) => void };
                        }
                    ).path;
                    if (
                        pixelEraseModeRef.current &&
                        !drawModeRef.current &&
                        path?.set
                    ) {
                        path.set({
                            globalCompositeOperation: "destination-out",
                            stroke: PIXEL_ERASE_BRUSH_STROKE,
                        });
                        drawCanvas!.requestRenderAll();
                    }
                });

                const d = drawModeRef.current;
                const e = eraseModeRef.current;
                const p = panModeRef.current;
                const l = lineModeRef.current;
                drawCanvas.isDrawingMode = (d || px) && !e && !p && !l;
                if (p) {
                    drawCanvas.defaultCursor = "grab";
                } else if ((d || px || l) && !e) {
                    drawCanvas.selection = false;
                    drawCanvas.defaultCursor = "crosshair";
                } else if (e) {
                    drawCanvas.defaultCursor = "cell";
                } else {
                    drawCanvas.defaultCursor = "default";
                }

                lastDisplayCssRef.current = null;
                applyDisplayScale();
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (!disposed) centerMapScroll();
                    });
                });
                ro = new ResizeObserver(() => scheduleDisplayScale());
                ro.observe(wrap);
                emitToolbarState();
                if (!disposed) setFabricReady(true);
            } catch {
                if (!disposed) {
                    releaseMountCanvases();
                    setStoragePreviewError(true);
                }
            }
        };

        void mount();

        return () => {
            disposed = true;
            viewZoomRef.current = 1;
            lastDisplayCssRef.current = null;
            if (displayScaleRafRef.current != null) {
                cancelAnimationFrame(displayScaleRafRef.current);
                displayScaleRafRef.current = null;
            }
            setFabricReady(false);
            ro?.disconnect();
            ro = null;
            releaseMountCanvases();
            safeClearElement(wrap);
        };
    }, [
        storagePreviewApiUrl,
        companionPreviewApiUrl,
        storageDims?.w,
        storageDims?.h,
        centerMapScroll,
        scheduleDisplayScale,
    ]);

    /** Re-center once the scrollable size is known (layout after first paint). */
    useEffect(() => {
        if (!fabricReady || !storageDims) return;
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                centerMapScroll();
            });
        });
        return () => cancelAnimationFrame(id);
    }, [fabricReady, storageDims?.w, storageDims?.h, centerMapScroll]);

    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        const dims = storageDims;
        if (!canvas || !dims || !fabricReady) return;

        const w = brushWidthFromBrushSlider(brushSize, dims.w, dims.h);
        const brush = canvas.freeDrawingBrush;
        /** Paint brush and pixel eraser are mutually exclusive; paint wins if both flags were true. */
        const wantPixel = pixelEraseMode && !drawMode;
        if (brush instanceof PencilBrush) {
            const isPixelEraseBrush = brush instanceof PixelEraseLiveBrush;
            if (wantPixel === isPixelEraseBrush) {
                brush.color = wantPixel
                    ? PIXEL_ERASE_BRUSH_STROKE
                    : MAP_EDITOR_PEN_HEX[penColor];
                brush.width = w;
                return;
            }
        }
        canvas.freeDrawingBrush = createFreeDrawingBrush(
            canvas,
            wantPixel,
            penColor,
            w,
        );
    }, [brushSize, storageDims, fabricReady, penColor, pixelEraseMode, drawMode]);

    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || !storageDims || !fabricReady) return;

        canvas.isDrawingMode = (drawMode || pixelEraseMode) && !eraseMode && !panMode && !lineMode;
        if (panMode) {
            canvas.defaultCursor = "grab";
        } else if ((drawMode || pixelEraseMode || lineMode) && !eraseMode) {
            canvas.selection = false;
            canvas.defaultCursor = "crosshair";
        } else if (eraseMode) {
            canvas.defaultCursor = "cell";
        } else {
            canvas.defaultCursor = "default";
        }
    }, [drawMode, eraseMode, panMode, pixelEraseMode, lineMode, storageDims, fabricReady]);

    const lineStrokeWidth = useCallback(() => {
        const dims = storageDims;
        const canvas = fabricCanvasRef.current;
        const w = dims?.w ?? canvas?.getWidth() ?? 1;
        const h = dims?.h ?? canvas?.getHeight() ?? 1;
        return brushWidthFromBrushSlider(brushSizeRef.current, w, h);
    }, [storageDims]);

    const createSketchLine = useCallback(
        (x1: number, y1: number, x2: number, y2: number, preview = false) =>
            new Line([x1, y1, x2, y2], {
                stroke: MAP_EDITOR_PEN_HEX[penColorRef.current] ?? MAP_EDITOR_PEN_HEX.black,
                strokeWidth: lineStrokeWidth(),
                fill: "",
                selectable: false,
                evented: !preview,
                strokeLineCap: "round",
                strokeLineJoin: "round",
            }),
        [lineStrokeWidth],
    );

    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || !storageDims || !fabricReady || !lineMode) return;

        const state = {
            active: false,
            startX: 0,
            startY: 0,
            preview: null as Line | null,
        };

        const scenePoint = (e: Event) => canvas.getScenePoint(e as MouseEvent);

        const onDown = (opt: { e?: Event }) => {
            if (
                !lineModeRef.current ||
                panModeRef.current ||
                eraseModeRef.current ||
                drawModeRef.current ||
                pixelEraseModeRef.current ||
                !opt.e
            ) {
                return;
            }
            const p = scenePoint(opt.e);
            state.active = true;
            state.startX = p.x;
            state.startY = p.y;
            state.preview = createSketchLine(p.x, p.y, p.x, p.y, true);
            canvas.add(state.preview);
            canvas.requestRenderAll();
        };

        const onMove = (opt: { e?: Event }) => {
            if (!state.active || !state.preview || !opt.e) return;
            const p = scenePoint(opt.e);
            state.preview.set({ x2: p.x, y2: p.y });
            state.preview.setCoords();
            canvas.requestRenderAll();
        };

        const finishLine = (opt: { e?: Event }) => {
            if (!state.active) return;
            state.active = false;
            const preview = state.preview;
            state.preview = null;
            if (!preview) return;

            let endX = preview.x2;
            let endY = preview.y2;
            if (opt.e) {
                const p = scenePoint(opt.e);
                endX = p.x;
                endY = p.y;
            }

            canvas.remove(preview);
            const dist = Math.hypot(endX - state.startX, endY - state.startY);
            if (dist >= 2) {
                canvas.add(createSketchLine(state.startX, state.startY, endX, endY));
            }
            canvas.requestRenderAll();
        };

        canvas.on("mouse:down", onDown);
        canvas.on("mouse:move", onMove);
        canvas.on("mouse:up", finishLine);
        return () => {
            canvas.off("mouse:down", onDown);
            canvas.off("mouse:move", onMove);
            canvas.off("mouse:up", finishLine);
            if (state.preview) {
                canvas.remove(state.preview);
                state.preview = null;
                canvas.requestRenderAll();
            }
        };
    }, [createSketchLine, fabricReady, lineMode, storageDims]);

    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || !storageDims || !fabricReady) return;

        const onEraseDown = (opt: { target?: FabricObject | null }) => {
            if (!eraseModeRef.current || panModeRef.current) return;
            const t = opt.target;
            if (t) {
                canvas.remove(t);
                canvas.requestRenderAll();
            }
        };
        canvas.on("mouse:down", onEraseDown);
        return () => {
            canvas.off("mouse:down", onEraseDown);
        };
    }, [storageDims, fabricReady]);

    /** Let pointer events reach the scrollable wrap while pan mode is active. */
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap || !fabricReady) return;
        const canvases = wrap.querySelectorAll("canvas");
        canvases.forEach((c) => {
            if (panMode) {
                c.style.pointerEvents = "none";
            } else {
                c.style.pointerEvents = "";
            }
        });
    }, [panMode, fabricReady]);

    useEffect(() => {
        if (!panMode) setPanPointerDown(false);
    }, [panMode]);

    /** Drag on the scrollable area to pan when pan tool is active. */
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap || !panMode || !fabricReady) return;

        const state = { active: false, x: 0, y: 0 };

        const onDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            state.active = true;
            state.x = e.clientX;
            state.y = e.clientY;
            setPanPointerDown(true);
            try {
                wrap.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        };

        const onMove = (e: PointerEvent) => {
            if (!state.active) return;
            const dx = e.clientX - state.x;
            const dy = e.clientY - state.y;
            state.x = e.clientX;
            state.y = e.clientY;
            wrap.scrollLeft -= dx;
            wrap.scrollTop -= dy;
        };

        const end = (e: PointerEvent) => {
            if (!state.active) return;
            state.active = false;
            setPanPointerDown(false);
            try {
                wrap.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        };

        wrap.addEventListener("pointerdown", onDown);
        wrap.addEventListener("pointermove", onMove);
        wrap.addEventListener("pointerup", end);
        wrap.addEventListener("pointercancel", end);

        return () => {
            wrap.removeEventListener("pointerdown", onDown);
            wrap.removeEventListener("pointermove", onMove);
            wrap.removeEventListener("pointerup", end);
            wrap.removeEventListener("pointercancel", end);
        };
    }, [panMode, fabricReady]);

    /** Two-finger pinch (touch), Ctrl/Cmd+wheel (trackpad), and Safari gesture pinch. */
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap || !storagePreviewApiUrl || !storageDims || !fabricReady) return;

        let pinchAnchorDist = 0;
        let pinchAnchorZoom = 1;
        let gestureAnchorZoom = 1;

        const clampZoom = (z: number) =>
            Math.min(MAX_VIEW_ZOOM, Math.max(MIN_VIEW_ZOOM, Number(z.toFixed(4))));

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                pinchAnchorDist = touchPairDistance(e.touches);
                pinchAnchorZoom = viewZoomRef.current;
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2 && pinchAnchorDist > 1) {
                e.preventDefault();
                const dist = touchPairDistance(e.touches);
                if (dist > 0) {
                    const ratio = dist / pinchAnchorDist;
                    viewZoomRef.current = clampZoom(pinchAnchorZoom * ratio);
                    scheduleDisplayScale();
                }
            }
        };

        const onTouchEndOrCancel = (e: TouchEvent) => {
            if (e.touches.length < 2) {
                pinchAnchorDist = 0;
            }
        };

        const onWheel = (e: WheelEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            const zoomIn = e.deltaY < 0;
            viewZoomRef.current = clampZoom(
                viewZoomRef.current * (zoomIn ? WHEEL_ZOOM_FACTOR : 1 / WHEEL_ZOOM_FACTOR),
            );
            scheduleDisplayScale();
        };

        const onGestureStart = (e: Event) => {
            e.preventDefault();
            gestureAnchorZoom = viewZoomRef.current;
        };

        const onGestureChange = (e: Event) => {
            e.preventDefault();
            const sc = (e as unknown as { scale?: number }).scale;
            if (typeof sc === "number" && sc > 0) {
                viewZoomRef.current = clampZoom(gestureAnchorZoom * sc);
                scheduleDisplayScale();
            }
        };

        const onGestureEnd = (e: Event) => {
            e.preventDefault();
        };

        wrap.addEventListener("touchstart", onTouchStart, { passive: true });
        wrap.addEventListener("touchmove", onTouchMove, { passive: false });
        wrap.addEventListener("touchend", onTouchEndOrCancel);
        wrap.addEventListener("touchcancel", onTouchEndOrCancel);
        wrap.addEventListener("wheel", onWheel, { passive: false });
        wrap.addEventListener("gesturestart", onGestureStart);
        wrap.addEventListener("gesturechange", onGestureChange);
        wrap.addEventListener("gestureend", onGestureEnd);

        return () => {
            wrap.removeEventListener("touchstart", onTouchStart);
            wrap.removeEventListener("touchmove", onTouchMove);
            wrap.removeEventListener("touchend", onTouchEndOrCancel);
            wrap.removeEventListener("touchcancel", onTouchEndOrCancel);
            wrap.removeEventListener("wheel", onWheel);
            wrap.removeEventListener("gesturestart", onGestureStart);
            wrap.removeEventListener("gesturechange", onGestureChange);
            wrap.removeEventListener("gestureend", onGestureEnd);
        };
    }, [storagePreviewApiUrl, storageDims, fabricReady, scheduleDisplayScale]);

    const undoSketch = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const objects = canvas.getObjects();
        const last = objects[objects.length - 1];
        if (last) {
            canvas.remove(last);
            canvas.requestRenderAll();
            emitToolbarState();
        }
    }, [emitToolbarState]);

    const clearAllSketch = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        for (const o of [...canvas.getObjects()]) {
            canvas.remove(o);
        }
        canvas.requestRenderAll();
        emitToolbarState();
    }, [emitToolbarState]);

    const saveBmp = useCallback(async () => {
        if (!storageEdit?.baseUrl || !storageDerived || !storageDims || saveInFlightRef.current)
            return;
        if (!isMapEditorGridCellAligned(storageGrid?.source)) {
            addToast({
                type: "error",
                duration: 6000,
                message:
                    "Cannot save: map preview dimensions unknown. Check preview-bmp loads for this map.",
            });
            return;
        }
        const rel = saveBmpRelPath.trim().replace(/\\/g, "/");
        if (!/\.(bmp|png)$/i.test(rel)) {
            addToast({ type: "error", message: "Save path must end with .png or .bmp" });
            return;
        }
        if (rel.includes("..") || rel.startsWith("/")) {
            addToast({ type: "error", message: "Invalid relative path" });
            return;
        }
        const drawCanvas = fabricCanvasRef.current;
        const mapStatic = mapStaticCanvasRef.current;
        if (!drawCanvas || !mapStatic) {
            addToast({ type: "error", message: "Editor not ready" });
            return;
        }
        const { w: iw, h: ih } = storageDims;
        const layoutNatural = editorLayoutNaturalRef.current;
        const variantNatural = variantNaturalRef.current;
        const usesCompanionGeometry = usesCompanionGeometryRef.current;
        const gridFillLayout = editorUsesGridFillLayout(storageGrid?.source);
        const aurora2dGrid = isAurora2dExportGridSource(storageGrid?.source);
        const strokeGridDirect =
            aurora2dGrid ||
            Boolean(
                layoutNatural && previewMatchesGridPixelSize(layoutNatural.w, layoutNatural.h, iw, ih),
            );
        const needsNav2Resample = Boolean(
            layoutNatural &&
            !strokeGridDirect &&
            previewNeedsNav2ResampleExport(layoutNatural.w, layoutNatural.h, iw, ih),
        );
        const nav2Transform =
            layoutNatural != null && !strokeGridDirect
                ? computeCanvasContainToFillGridTransform(
                    layoutNatural.w,
                    layoutNatural.h,
                    iw,
                    ih,
                )
                : { kx: 1, ky: 1, tx: 0, ty: 0 };
        const liveGridPx = liveOccupancyGridDims(editorGridMetadata, storageEdit?.liveMapGrid);
        const variantAspectMismatch = variantAspectDiffersFromLayout(
            variantNatural,
            layoutNatural,
        );
        const sketchBounds = sketchBoundsFromCanvas(drawCanvas);
        saveInFlightRef.current = true;
        setSaveBusy(true);
        let exportPath: "nav2_cell_aligned" | "merge_composite" | "variant_passthrough" =
            "nav2_cell_aligned";
        try {
            let blob: Blob | null = null;
            let exportLayerError: string | undefined;
            const geometryUrl = geometryPreviewUrlRef.current ?? storagePreviewApiUrl;
            const exportImages = buildMaskExportImages({
                geometryUrl,
                variantPreviewUrl: storagePreviewApiUrl,
                layoutNatural,
                companionNatural: companionNaturalRef.current,
                variantNatural: variantNaturalRef.current,
                usesCompanionGeometry,
            });
            const noNewStrokes = !sketchBounds || sketchBounds.objectCount === 0;
            const variantPreviewUrl =
                usesCompanionGeometry &&
                    storagePreviewApiUrl &&
                    storagePreviewApiUrl !== geometryUrl
                    ? storagePreviewApiUrl
                    : null;
            if (noNewStrokes && variantPreviewUrl) {
                try {
                    const existing = await fetchPreviewImageBlob(variantPreviewUrl);
                    const existingPx = await loadBlobImageDimensions(existing);
                    if (existingPx && existingPx.w === iw && existingPx.h === ih) {
                        blob = existing;
                        exportPath = "variant_passthrough";
                    }
                } catch {
                    blob = null;
                }
            }
            if (!blob && layoutNatural && exportImages.length > 0) {
                try {
                    blob = await exportNav2CellAlignedMaskFromEditor(
                        drawCanvas,
                        layoutNatural,
                        iw,
                        ih,
                        exportImages,
                        { strokeGridDirect },
                    );
                    if (blob) {
                        exportPath = "nav2_cell_aligned";
                    }
                } catch (e) {
                    exportLayerError =
                        e instanceof Error ? e.message : "exportNav2CellAlignedMaskFromEditor failed";
                    blob = null;
                }
            }
            if (!blob && !needsNav2Resample) {
                try {
                    blob = await mergeMapAndAnnotationsToBlob(mapStatic, drawCanvas, iw, ih);
                    if (blob) {
                        exportPath = "merge_composite";
                    }
                } catch {
                    blob = null;
                }
            }
            if (!blob) {
                addToast({ type: "error", message: "Could not export image" });
                return;
            }
            const outputPx = await loadBlobImageDimensions(blob);
            const filterMetaForYaml = pickMapEditorGridMetadata(
                mapMetadata,
                storageEdit?.liveMapGrid,
            );
            const filterGeom = mapFilterGeometryFromMetadata(filterMetaForYaml);
            const alignmentHints: string[] = [];
            if (outputPx && (outputPx.w !== iw || outputPx.h !== ih)) {
                alignmentHints.push(
                    `saved PNG ${outputPx.w}×${outputPx.h}px does not match editor grid ${iw}×${ih} cells`,
                );
            }
            if (
                aurora2dGrid &&
                liveGridPx &&
                (liveGridPx.width !== iw || liveGridPx.height !== ih)
            ) {
                alignmentHints.push(
                    `editor uses Aurora 2D export ${iw}×${ih}px; live /map_metadata is ${liveGridPx.width}×${liveGridPx.height} cells`,
                );
            }
            if (variantAspectMismatch && usesCompanionGeometry) {
                alignmentHints.push(
                    "variant PNG aspect differs from parent STCM companion; layout uses companion BMP geometry",
                );
            }
            if (needsNav2Resample && exportPath === "merge_composite") {
                alignmentHints.push(
                    "letterboxed map was saved via merge (no Nav2 resample); mask may be misaligned",
                );
            }
            if (sketchBounds) {
                const gridFromStroke = strokeGridDirect
                    ? {
                        minX: sketchBounds.minX,
                        minY: sketchBounds.minY,
                        maxX: sketchBounds.maxX,
                        maxY: sketchBounds.maxY,
                    }
                    : {
                        minX:
                            sketchBounds.minX * (nav2Transform?.kx ?? 1) +
                            (nav2Transform?.tx ?? 0),
                        minY:
                            sketchBounds.minY * (nav2Transform?.ky ?? 1) +
                            (nav2Transform?.ty ?? 0),
                        maxX:
                            sketchBounds.maxX * (nav2Transform?.kx ?? 1) +
                            (nav2Transform?.tx ?? 0),
                        maxY:
                            sketchBounds.maxY * (nav2Transform?.ky ?? 1) +
                            (nav2Transform?.ty ?? 0),
                    };
                if (
                    gridFromStroke.minX < -0.5 ||
                    gridFromStroke.minY < -0.5 ||
                    gridFromStroke.maxX > iw + 0.5 ||
                    gridFromStroke.maxY > ih + 0.5
                ) {
                    alignmentHints.push(
                        `sketch extends outside grid after transform: ` +
                        `[${gridFromStroke.minX.toFixed(1)}, ${gridFromStroke.minY.toFixed(1)}]–` +
                        `[${gridFromStroke.maxX.toFixed(1)}, ${gridFromStroke.maxY.toFixed(1)}]`,
                    );
                }
            }
            logMapEditorSaveDebug({
                export_path: exportPath,
                ...(isFilterMaskDebugEnabled() && exportLayerError
                    ? { export_layer_error: exportLayerError }
                    : {}),
                ...(isFilterMaskDebugEnabled() && exportImages.length > 0
                    ? { export_image_urls: exportImages.map((i) => i.url) }
                    : {}),
                grid_cells: {
                    width: iw,
                    height: ih,
                    source: storageGrid?.source,
                },
                variant_natural_px: variantNatural
                    ? { width: variantNatural.w, height: variantNatural.h }
                    : null,
                layout_natural_px: layoutNatural
                    ? { width: layoutNatural.w, height: layoutNatural.h }
                    : null,
                uses_companion_geometry: usesCompanionGeometry,
                editor_grid_fill: gridFillLayout,
                editor_aurora_2d_grid: aurora2dGrid,
                live_grid_cells: liveGridPx,
                variant_aspect_mismatch: variantAspectMismatch,
                editor_canvas_px: {
                    width: drawCanvas.getWidth(),
                    height: drawCanvas.getHeight(),
                },
                needs_nav2_resample: needsNav2Resample,
                nav2_transform: nav2Transform,
                sketch_bounds_canvas_px: sketchBounds,
                output_blob_px: outputPx
                    ? { width: outputPx.w, height: outputPx.h }
                    : null,
                filter_geometry: filterGeom
                    ? {
                        resolution: filterGeom.resolution,
                        origin_x: filterGeom.originX,
                        origin_y: filterGeom.originY,
                        origin_yaw: filterGeom.originYaw,
                    }
                    : null,
                alignment_hints:
                    alignmentHints.length > 0
                        ? alignmentHints
                        : ["export dimensions match live grid"],
            });
            const relEditor = storageEdit.relPath.replace(/\\/g, "/").trim();
            const sourceStcm =
                storageEdit.parentStcmRelPath?.trim() ||
                (relEditor.toLowerCase().endsWith(".stcm") ? relEditor : undefined);
            const result = await postSaveVariantImage(storageEdit.baseUrl, rel, blob, {
                sourceStcmRelPath: sourceStcm,
                mapFilterGeometry: filterGeom,
            });
            addToast({
                type: "success",
                message: `Saved image: ${result.written}`,
            });
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Save failed",
            });
        } finally {
            saveInFlightRef.current = false;
            setSaveBusy(false);
        }
    }, [
        storageEdit?.baseUrl,
        storageEdit?.relPath,
        storageEdit?.parentStcmRelPath,
        storageDerived,
        storageDims,
        saveBmpRelPath,
        storageGrid?.source,
        editorGridMetadata,
        storagePreviewApiUrl,
        addToast,
    ]);

    useEffect(() => {
        onSaveBusyChange?.(saveBusy);
    }, [saveBusy, onSaveBusyChange]);

    useImperativeHandle(
        ref,
        () => ({
            saveBmp,
            undoSketch,
            clearAllSketch,
            zoomViewIn,
            zoomViewOut,
            resetViewZoom,
        }),
        [saveBmp, undoSketch, clearAllSketch, zoomViewIn, zoomViewOut, resetViewZoom],
    );

    if (storageEdit?.baseUrl && storageEdit?.relPath) {
        if (storagePreviewError) {
            return (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-black90 px-4">
                    <p className="text-h5 text-black60 text-center">
                        Could not load map preview (same URL as Map Manager). Check the network
                        connection to the ARC and that the companion preview image exists for this map.
                    </p>
                </div>
            );
        }
        if (!storageDerived) {
            return (
                <div className="flex h-full w-full items-center justify-center bg-black90">
                    <p className="text-h5 text-black60">Loading map image…</p>
                </div>
            );
        }
        const showAligningHint =
            storageGrid != null && !isMapEditorGridCellAligned(storageGrid.source);
        return (
            <div className="relative h-full min-h-0 w-full min-w-0">
                {showAligningHint ? (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black100/90 px-4">
                        <p className="max-w-md text-center text-h5 text-amber-400">
                            Waiting for map preview — could not read preview-bmp dimensions for
                            this file.
                        </p>
                    </div>
                ) : null}
                <div
                    ref={wrapRef}
                    className={`relative box-border h-full min-h-0 w-full min-w-0 overflow-auto overscroll-contain bg-black100 ${panMode
                            ? `touch-none ${panPointerDown ? "cursor-grabbing" : "cursor-grab"}`
                            : ""
                        }`}
                />
            </div>
        );
    }

    return (
        <div className="h-full w-full">
            <MapView
                mapId={mapMetadata?.id}
                mapMetadata={mapMetadata}
                mapViewData={storageEditActive ? null : mapImageUrl}
                fitMapToBoundsOnLoad
            />
        </div>
    );
});

MapEditor.displayName = "MapEditor";

export default MapEditor;
